/* Plug wishlist on Rivo Favorites (replaced Swym in 2026-10; settings.wishlist_enabled).
   One delegated handler drives every [data-pf-wish] heart, including hearts rendered after load
   (product swaps on the pilot PDP, iPhone Match). Signed-in customers save through Rivo's storefront
   API (window.RivoAPI, the ba-loy app proxy). Guests are sent to sign in with the save parked in
   Rivo's own localStorage key, which Rivo's embed replays within 30 minutes of signing in. The
   My Tech Wishlist tab reads the same favorites through listSource(). */
(function (root, factory) {
  const api = factory(root);
  if (root) root.PfWishlist = api;
  if (typeof module === "object" && module.exports) module.exports = api;
})(typeof window !== "undefined" ? window : null, function (root) {
  const PENDING_KEY = "rivo_logged_out_favorite_product";
  const LIST_ID = "favorites";
  const READY_TIMEOUT_MS = 10000;

  const state = {
    config: {},
    favorites: new Map(),
    status: "idle",
    loading: null,
    listeners: new Set(),
    navigate: (url) => root.location.assign(url),
  };

  const id = (value) => (value == null || value === "" ? "" : String(value));

  function readConfig(doc) {
    const node = doc?.getElementById("PfWishlistConfig");
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || "{}");
    } catch {
      return {};
    }
  }

  // Rivo answers { products: [...] }; anything else is a failed request (RivoAPI resolves {} on errors).
  function normalize(response) {
    if (!response || !Array.isArray(response.products)) return null;
    return response.products
      .filter((product) => product && product.remote_id != null)
      .map((product) => {
        const variant = Array.isArray(product.variants) ? product.variants[0] : null;
        return {
          productId: id(product.remote_id),
          variantId: id(
            product.variant_id ?? product.remote_variant_id ?? variant?.remote_id ?? variant?.id
          ),
          handle: product.handle || "",
          title: product.title || "",
          image: product.image_url || "",
          price: variant?.price ?? null,
          compareAtPrice: product.compare_at_price ?? null,
        };
      });
  }

  function rivoApi() {
    return root?.RivoAPI && typeof root.RivoAPI.favorite_products === "function"
      ? root.RivoAPI
      : null;
  }

  function whenRivoReady() {
    if (rivoApi()) return Promise.resolve(rivoApi());
    return new Promise((resolve, reject) => {
      const done = () => {
        root.document.removeEventListener("rivo-js-loaded", loaded);
        root.clearTimeout(timer);
      };
      const loaded = () => {
        done();
        if (rivoApi()) resolve(rivoApi());
        else reject(new Error("rivo-unavailable"));
      };
      const timer = root.setTimeout(() => {
        done();
        if (rivoApi()) resolve(rivoApi());
        else reject(new Error("rivo-unavailable"));
      }, READY_TIMEOUT_MS);
      root.document.addEventListener("rivo-js-loaded", loaded);
    });
  }

  function isSignedIn() {
    return state.config.customer === true;
  }

  function has(productId) {
    return state.favorites.has(id(productId));
  }

  function list() {
    return [...state.favorites.values()];
  }

  function emit() {
    paint(root?.document);
    state.listeners.forEach((listener) => listener(list(), state.status));
    root?.document?.dispatchEvent(
      new root.CustomEvent("pf:wishlist:change", {
        detail: { status: state.status, count: state.favorites.size, items: list() },
      })
    );
  }

  function load() {
    if (!isSignedIn()) {
      state.status = "guest";
      state.favorites.clear();
      emit();
      return Promise.resolve(list());
    }
    if (state.loading) return state.loading;
    state.status = "loading";
    state.loading = whenRivoReady()
      .then((api) => api.favorite_products("db", state.config.currency || null))
      .then((response) => {
        const items = normalize(response);
        if (!items) throw new Error("rivo-favorites");
        state.favorites = new Map(items.map((item) => [item.productId, item]));
        state.status = "ready";
        return list();
      })
      .catch((error) => {
        state.status = "error";
        throw error;
      })
      .finally(() => {
        state.loading = null;
        emit();
      });
    return state.loading;
  }

  function goToSignIn(productId, variantId) {
    try {
      root.localStorage.setItem(
        PENDING_KEY,
        JSON.stringify({
          productId: id(productId),
          variantId: id(variantId) || undefined,
          savedAt: Date.now(),
        })
      );
    } catch {
      // Private mode: the shopper still signs in, they just tap the heart again after.
    }
    const login = new URL(state.config.loginUrl || "/account/login", root.location.origin);
    login.searchParams.set("return_to", root.location.pathname + root.location.search);
    state.navigate(login.href);
  }

  // Optimistic: the heart flips at once, Rivo is asked, and the list is re-read to settle on
  // Rivo's answer (its API resolves {} instead of rejecting when a save fails).
  async function toggle(productId, variantId) {
    const key = id(productId);
    if (!key) return false;
    if (!isSignedIn()) {
      goToSignIn(key, variantId);
      return false;
    }
    const adding = !has(key);
    if (adding) state.favorites.set(key, { productId: key, variantId: id(variantId) });
    else state.favorites.delete(key);
    emit();
    try {
      const api = await whenRivoReady();
      if (adding) await api.add_favorite_product(Number(key), variantId ? Number(variantId) : null);
      else await api.remove_favorite_product(Number(key));
    } finally {
      await load().catch(() => {});
    }
    return has(key);
  }

  function labelFor(button, pressed) {
    const name = button.dataset.pfWishName || "";
    const add = state.config.labels?.add || "Save [name] to your wishlist";
    const remove = state.config.labels?.remove || "Remove [name] from your wishlist";
    return (pressed ? remove : add).replace("[name]", name).replace(/\s+/g, " ").trim();
  }

  function heartsIn(scope) {
    if (!scope?.querySelectorAll) return [];
    const buttons = scope.matches?.("[data-pf-wish]") ? [scope] : [];
    buttons.push(...scope.querySelectorAll("[data-pf-wish]"));
    return buttons;
  }

  function paintHearts(buttons) {
    buttons.forEach((button) => {
      const pressed = has(button.dataset.productId);
      button.setAttribute("aria-pressed", pressed ? "true" : "false");
      button.setAttribute("aria-label", labelFor(button, pressed));
      button.classList.toggle("is-wished", pressed);
    });
  }

  function paint(scope) {
    if (!scope?.querySelectorAll) return;
    paintHearts(heartsIn(scope));
    paintCount();
  }

  function paintCount() {
    root?.document?.querySelectorAll("[data-pf-wish-count]").forEach((badge) => {
      const count = isSignedIn() ? state.favorites.size : 0;
      badge.dataset.count = String(count);
      badge.textContent = count ? String(count) : "";
    });
  }

  // On a product page the add-to-cart form holds the variant picked since the page rendered, so it
  // wins over the heart's rendered data-variant-id. Cards have no form and use their own id.
  function variantFor(button) {
    return (
      button.closest("form")?.querySelector('[name="id"]')?.value ||
      button
        .closest("[data-pf-experience], product-info, pf-pilot-product-info, .product")
        ?.querySelector('form[action*="/cart/add"] [name="id"]')?.value ||
      button.dataset.variantId ||
      ""
    );
  }

  function onClick(event) {
    const button = event.target.closest?.("[data-pf-wish]");
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    toggle(button.dataset.productId, variantFor(button));
  }

  // The My Tech Wishlist renderer (assets/pf-for-you-wishlist.js) was written against Swym's list
  // calls. This keeps that contract over Rivo: one list, items in Swym's field names.
  function listSource() {
    const asItem = (item) => ({
      epi: item.variantId,
      empi: item.productId,
      du: item.handle ? `/products/${item.handle}` : "",
      dt: item.title,
      iu: item.image,
      pr: item.price,
    });
    return {
      fetchLists({ callbackFn, errorFn }) {
        load()
          .then(() =>
            callbackFn([
              {
                lid: LIST_ID,
                lname: state.config.labels?.list || "Favorites",
                listcontents: list().map(asItem),
              },
            ])
          )
          .catch(() => errorFn?.());
      },
      deleteFromList(lid, item, done, failed) {
        whenRivoReady()
          .then((api) => api.remove_favorite_product(Number(item.empi)))
          .then(() => load())
          .then(() => (has(item.empi) ? failed?.() : done?.()))
          .catch(() => failed?.());
      },
      isCollectionsEnabled: () => false,
      platform: { isLoggedIn: isSignedIn },
    };
  }

  function onChange(listener) {
    state.listeners.add(listener);
    return () => state.listeners.delete(listener);
  }

  function start() {
    const doc = root?.document;
    if (!doc) return;
    state.config = readConfig(doc);
    doc.addEventListener("click", onClick);
    // Rivo's own panel (favorites page, post-login replay) changes the list without us.
    doc.addEventListener("rivo-accounts:items:updated", () => load().catch(() => {}));
    // New hearts (product swaps, iPhone Match) and hearts retargeted to another product (the pilot's
    // product-context refresh rewrites data-product-id) repaint from the saved list.
    // One pass per batch: only nodes that carry hearts repaint, and the count badge (a whole-
    // document lookup) refreshes once, not once per added node.
    new root.MutationObserver((records) => {
      const hearts = new Set();
      let badge = false;
      records.forEach((record) => {
        if (record.type === "attributes") heartsIn(record.target).forEach((b) => hearts.add(b));
        else
          record.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            heartsIn(node).forEach((b) => hearts.add(b));
            badge ||= Boolean(
              node.matches?.("[data-pf-wish-count]") || node.querySelector?.("[data-pf-wish-count]")
            );
          });
      });
      if (!hearts.size && !badge) return;
      paintHearts(hearts);
      paintCount();
    }).observe(doc.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-product-id"],
    });
    load().catch(() => {});
  }

  if (root?.document) {
    if (root.document.readyState === "loading")
      root.document.addEventListener("DOMContentLoaded", start);
    else start();
  }

  return { has, list, load, toggle, listSource, onChange, normalize, isSignedIn, state, start };
});
